import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { switcherCountLabel } from './switcherGames.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDown,
    faArrowRight,
    faCheck,
    faChevronRight,
    faMagnifyingGlass,
    faXmark
} from '@fortawesome/free-solid-svg-icons';

import { updateDashboardContext } from '../../redux-store/ReduxStore.js';
import InfoTooltip from '../../components/tooltip/InfoTooltip.jsx';
import CopyableId from '../../components/copyable-id/CopyableId.jsx';
import './ContextSwitcher.css';

/** The widest the shared name column gets before a long game name truncates. */
const NAME_COLUMN_MAX_PX = 240;

const getInitial = (label) => {
    if (!label) return '?';
    return String(label).trim().charAt(0).toUpperCase();
};

const LetterBadge = ({ label, tone }) => (
    <span className={`ctx-letter-badge ctx-letter-badge-${tone}`}>
        {getInitial(label)}
    </span>
);

export const ContextSwitcher = ({
    // The chips/cards to render, in order. Each is
    // { key, label, pluralLabel, singular, items, selectedValue, selectedLabel, onSelect, tone }.
    // Callers decide what a scope shows — see NAV_SCOPES.headerCards.
    //
    // Optional, for the chip in the bar only (the popover's cards keep the plain badge and label):
    //   renderChipBadge  the chip's badge (e.g. the game's icon tile); returning null drops it
    //   chipLabel        the chip's text when it should differ from selectedLabel ("All players")
    //   muted            a quieter chip (the org leading a breadcrumb)
    //   rich             the context's main chip: bolder label, room for `chipExtras`
    //   chipExtras       nodes shown BESIDE the switcher, outside its clickable box (status /
    //                    access badges, a one-line description) — the box is just the selection
    //
    // Optional, for the popover:
    //   footerLink       { label, hint, onClick } — a link under the card's list (the game card's
    //                    "Browse all games", to the page that lists the org's other games)
    cards = [],

    // Read-only mode: chips render but can't be clicked, and the popover
    // never opens. Used on form pages so the user sees the context
    // their record will land in without being able to switch it mid-flow.
    disabled = false,

    // 'header' is the top-bar breadcrumb pill. 'sidebar' is the full-width variant mounted in the
    // nav rail (see SidebarSwitcherSlot) — same component and handlers, different metrics. 'picker'
    // is a bordered single-card control on the right of the bar (the geography picker): its popover
    // opens leftward.
    variant = 'header',
    // Between chips: 'slash' (LNG / Tetris) or 'chevron' (LNG › Tetris).
    separator = 'slash',
    // Only ONE instance may answer the "open the switcher" one-shot, or every mounted copy pops its
    // dropdown at once. The header instance owns it; the sidebar copy passes false.
    honorOpenRequest = true,
}) => {
    const dispatch = useDispatch();
    // One-shot request raised by a game page's access card's "Open game switcher" button — opens this
    // dropdown straight to the game card (mirrors DashboardMenu's openWidgetMenuRequest).
    const openSwitcherRequest = useSelector(
        state => state.dashboardContext?.openContextSwitcherRequest ?? false
    );

    const [open, setOpen] = useState(false);
    const [activeCard, setActiveCard] = useState('game');
    const [search, setSearch] = useState('');
    // Whether the user has started editing the active card's field. Until they do, the field shows the
    // current selection's label; once they do, the field reflects exactly what they typed — including an
    // empty value — instead of snapping back to the selected label (the "can't clear the game" bug).
    const [searchDirty, setSearchDirty] = useState(false);
    const rootRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    useEffect(() => {
        // Reset the editing state whenever the dropdown opens/closes or the active card changes, so each
        // card starts by showing its current selection rather than stale typed text.
        setSearch('');
        setSearchDirty(false);
        if (!open) return;
        // Focus the active card's search input when opened or card switched
        const id = window.setTimeout(() => searchInputRef.current?.focus(), 10);
        return () => window.clearTimeout(id);
    }, [open, activeCard]);

    // Honor the "Open game switcher" cue from a game page's access card: open this dropdown on the game
    // card, then clear the one-shot flag so it doesn't re-fire. No-op (but still cleared) when the
    // switcher is disabled (e.g. form pages).
    const hasGameCard = cards.some(c => c.key === 'game');
    useEffect(() => {
        if (!honorOpenRequest || !openSwitcherRequest) return;
        if (!disabled) {
            setOpen(true);
            if (hasGameCard) setActiveCard('game');
        }
        dispatch(updateDashboardContext({ openContextSwitcherRequest: false }));
    }, [honorOpenRequest, openSwitcherRequest, disabled, hasGameCard, dispatch]);

    const cardConfig = useMemo(() => Object.fromEntries(cards.map(card => [card.key, {
        ...card,
        renderBadge: card.renderBadge
            || ((label) => <LetterBadge label={label} tone={card.tone} />),
    }])), [cards]);

    const visibleCards = cards.map(c => c.key);

    const effectiveActiveCard = visibleCards.includes(activeCard)
        ? activeCard
        : (visibleCards[0] || 'game');
    const activeConfig = cardConfig[effectiveActiveCard];

    const filtered = useMemo(() => {
        const items = activeConfig.items || [];
        if (!search.trim()) return items;
        const q = search.trim().toLowerCase();
        return items.filter(item => String(item.label).toLowerCase().includes(q));
    }, [activeConfig.items, search]);

    // Rows that carry an id (games) give every name the width of the longest one shown, so the ID badges
    // start at the same x down the list - beside the names, not pushed to the row's end. Measured from the
    // names' own text, so it shrinks again when a search hides the longest; capped so a long name truncates.
    const listRef = useRef(null);
    const [nameColWidth, setNameColWidth] = useState(null);
    useLayoutEffect(() => {
        if (!open || !listRef.current) return;
        const texts = listRef.current.querySelectorAll('.ctx-list-item-label--col .ctx-list-item-label-text');
        let widest = 0;
        texts.forEach(t => { widest = Math.max(widest, Math.ceil(t.getBoundingClientRect().width)); });
        const next = widest ? Math.min(widest, NAME_COLUMN_MAX_PX) : null;
        setNameColWidth(prev => (prev === next ? prev : next));
    }, [open, filtered, effectiveActiveCard]);

    if (visibleCards.length === 0) return null;

    // Chip click rules:
    //  - closed  → open with that card active
    //  - open, clicking a DIFFERENT chip → switch active card, stay open
    //  - open, clicking the SAME chip that's already active → close
    const handleChipClick = (key) => {
        if (disabled) return;
        if (!open) {
            setActiveCard(key);
            setSearch('');
            setOpen(true);
            return;
        }
        if (key === effectiveActiveCard) {
            setOpen(false);
            return;
        }
        setActiveCard(key);
        setSearch('');
    };

    const toggleCaret = () => {
        if (disabled) return;
        if (open) {
            setOpen(false);
            return;
        }
        // Land on the first visible card that hasn't been chosen yet (org → game), so the user gets
        // something actionable rather than a panel with nothing to pick from.
        let nextCard = visibleCards[visibleCards.length - 1];
        for (const key of visibleCards) {
            if (!cardConfig[key].selectedValue) {
                nextCard = key;
                break;
            }
        }
        setActiveCard(nextCard);
        setSearch('');
        setOpen(true);
    };

    const chipSlots = visibleCards.map((key, idx) => {
        const cfg = cardConfig[key];
        const chipText = cfg.chipLabel || cfg.selectedLabel || cfg.label;
        return (
            <React.Fragment key={key}>
                {idx > 0 && (separator === 'chevron'
                    ? <FontAwesomeIcon icon={faChevronRight} className="ctx-sep ctx-sep--chevron" aria-hidden="true" />
                    : <span className="ctx-sep">/</span>)}
                <button
                    type="button"
                    className={`ctx-chip ctx-chip-${cfg.tone} ${cfg.muted ? 'ctx-chip--muted' : ''} ${cfg.rich ? 'ctx-chip--rich' : ''} ${open && key === effectiveActiveCard ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                    onClick={() => handleChipClick(key)}
                    disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                >
                    {cfg.renderChipBadge ? cfg.renderChipBadge(cfg.selectedLabel) : cfg.renderBadge(cfg.selectedLabel)}
                    <span className="ctx-chip-label">
                        <InfoTooltip text={cfg.chipTooltip || cfg.selectedLabel || cfg.label}>
                            {chipText}
                        </InfoTooltip>
                    </span>
                </button>
            </React.Fragment>
        );
    });

    // What the chips describe but do not select (badges, a description): beside the switcher, not in it.
    const trailingExtras = visibleCards
        .filter(key => cardConfig[key].chipExtras)
        .map(key => <React.Fragment key={key}>{cardConfig[key].chipExtras}</React.Fragment>);

    // Cascade: after selecting, advance to the next visible card and clear search. The last card is
    // a leaf — nothing left to choose — so it closes the popover instead.
    const nextCardKey = (key) => visibleCards[visibleCards.indexOf(key) + 1] || key;

    const handleItemClick = (item) => {
        // A lifecycle-frozen scope (SUSPENDED or SUNSET, the scope's OWN administrative state) is
        // non-selectable — the Workspace pages behind it are fully locked either way, and
        // reactivation is a separate owner/co-owner-only action on Game Management, not something
        // reached by switching context into the scope.
        if (item.lifecycleState === 'SUNSET' || item.lifecycleState === 'SUSPENDED') {
            return;
        }
        activeConfig.onSelect(item.value);
        const nextKey = nextCardKey(effectiveActiveCard);
        if (nextKey && nextKey !== effectiveActiveCard && visibleCards.includes(nextKey)) {
            setActiveCard(nextKey);
        } else {
            // No further card to advance to (leaf selection) — close the popover.
            setOpen(false);
        }
        setSearch('');
        setSearchDirty(false);
    };

    const handleFooterClick = () => {
        setOpen(false);
        activeConfig.footerLink?.onClick?.();
    };

    const countLabel = switcherCountLabel({
        items: activeConfig.items || [],
        matched: search.trim() ? filtered.length : null,
    });

    return (
        <div className={`ctx-switcher ctx-switcher--${variant} ${trailingExtras.length ? 'ctx-switcher--with-extras' : ''} ${disabled ? 'disabled' : ''}`} ref={rootRef}>
            <div
                className={`ctx-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                aria-disabled={disabled || undefined}
            >
                {chipSlots}
                {!disabled && (
                    <button
                        type="button"
                        className="ctx-caret-btn"
                        onClick={toggleCaret}
                        aria-label="Open context switcher"
                    >
                        <FontAwesomeIcon icon={faAngleDown} className="ctx-caret" />
                    </button>
                )}
            </div>

            {trailingExtras.length > 0 && (
                <div className="ctx-switcher-extras">{trailingExtras}</div>
            )}

            {open && !disabled && (
                <div className="ctx-popover" role="dialog">
                    {/* Column count follows the visible cards — the grid used to be hardcoded to 3
                        (org/game/build) and left a dead column once build was removed. */}
                    <div className={`ctx-cards ctx-cards--${visibleCards.length}`}>
                        {visibleCards.map(key => {
                            const cfg = cardConfig[key];
                            const isActive = key === effectiveActiveCard;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className={`ctx-card ${isActive ? 'active' : ''} ctx-card-${cfg.tone}`}
                                    onClick={() => setActiveCard(key)}
                                >
                                    <div className="ctx-card-badge">
                                        {cfg.renderBadge(cfg.selectedLabel)}
                                    </div>
                                    <div className="ctx-card-text">
                                        <div className="ctx-card-label">{cfg.label}</div>
                                        {isActive ? (
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                className="ctx-card-search"
                                                value={searchDirty ? search : (cfg.selectedLabel || '')}
                                                placeholder={cfg.selectedLabel || `Select ${cfg.singular}`}
                                                onChange={(e) => { setSearch(e.target.value); setSearchDirty(true); }}
                                                onFocus={(e) => e.target.select()}
                                            />
                                        ) : (
                                            <div className="ctx-card-value">
                                                <InfoTooltip text={cfg.selectedLabel || ''}>
                                                    {cfg.selectedLabel || '—'}
                                                </InfoTooltip>
                                            </div>
                                        )}
                                    </div>
                                    {isActive && search && (
                                        <span
                                            className="ctx-card-clear"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSearch('');
                                                searchInputRef.current?.focus();
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faXmark} />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="ctx-list-header">
                        <span>All {activeConfig.pluralLabel}</span>
                        <span className="ctx-list-count">{countLabel}</span>
                    </div>

                    <div className="ctx-list" ref={listRef}>
                        {filtered.length === 0 ? (
                            <div className="ctx-empty">
                                <FontAwesomeIcon icon={faMagnifyingGlass} className="ctx-empty-icon" />
                                <div className="ctx-empty-text">
                                    No {activeConfig.singular} matches
                                    {search.trim() ? ` "${search.trim()}"` : ''}
                                </div>
                            </div>
                        ) : (
                            filtered.map((item) => {
                                const isSelected = item.value === activeConfig.selectedValue;
                                // The scope's OWN lifecycle freeze (honoring containment). Both SUNSET
                                // (terminal) and SUSPENDED (recoverable) are non-selectable — their
                                // Workspace pages are fully locked regardless, and reactivation happens
                                // from Game Management (owner/co-owner-gated), not by switching into it.
                                const isSunset = item.lifecycleState === 'SUNSET';
                                const isLifecycleSuspended = item.lifecycleState === 'SUSPENDED';
                                const isBlocked = isSunset || isLifecycleSuspended;
                                return (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={`ctx-list-item ${isSelected ? 'selected' : ''} ${isBlocked ? 'inaccessible' : ''}`}
                                        onClick={() => handleItemClick(item)}
                                        // Not `disabled`: a disabled <button> swallows clicks on its children,
                                        // which would kill the row's copy-the-id badge. The handleItemClick
                                        // guard makes the row body click a no-op either way.
                                        aria-disabled={isBlocked || undefined}
                                    >
                                        <span className="ctx-list-item-badge">
                                            {activeConfig.renderBadge(item.label)}
                                        </span>
                                        {item.copyId ? (
                                            <>
                                                {/* Every name the same width, so the ID badges line up beside them. */}
                                                <span
                                                    className="ctx-list-item-label ctx-list-item-label--col"
                                                    style={nameColWidth ? { width: nameColWidth } : undefined}
                                                >
                                                    <span className="ctx-list-item-label-text">{item.label}</span>
                                                </span>
                                                {/* [ ID | 12378637 ⧉ ] - copying never selects the row. */}
                                                <span className="ctx-list-item-id">
                                                    <CopyableId id={item.copyId} label={item.copyIdLabel || 'ID'} variant="badge" />
                                                </span>
                                            </>
                                        ) : (
                                            <span className="ctx-list-item-label">
                                                {item.label}
                                            </span>
                                        )}
                                        {isSelected && !isBlocked && (
                                            <FontAwesomeIcon icon={faCheck} className="ctx-list-item-check" />
                                        )}
                                        {isSunset ? (
                                            <span className="ctx-list-item-pill ctx-list-item-pill-sunset">
                                                Sunset
                                            </span>
                                        ) : isLifecycleSuspended ? (
                                            <span className="ctx-list-item-pill ctx-list-item-pill-suspended">
                                                Suspended
                                            </span>
                                        ) : item.statusLabel ? (
                                            // An org that is not running (Suspended / Archived): told, but
                                            // still selectable - its owners manage it from inside.
                                            <span className="ctx-list-item-pill ctx-list-item-pill-suspended">
                                                {item.statusLabel}
                                            </span>
                                        ) : null}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {activeConfig.footerLink && (
                        <button type="button" className="ctx-list-footer" onClick={handleFooterClick}>
                            <span className="ctx-list-footer-label">{activeConfig.footerLink.label}</span>
                            {activeConfig.footerLink.hint && (
                                <span className="ctx-list-footer-hint">{activeConfig.footerLink.hint}</span>
                            )}
                            <FontAwesomeIcon icon={faArrowRight} className="ctx-list-footer-icon" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContextSwitcher;
